import {
  Box,
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  type FormControlProp,
  HintIcon,
  NumericFormatField,
  RequiredTag,
  Row,
  SubmitButton,
  Text,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import {
  parseTargetFiscalYearTitle,
  rateByEvaluationDirection,
} from '@/features/mold/utils';
import {
  type EvaluationDirectionType,
  UpdateConsecutiveIndicatorFormSchema,
} from '@/features/project/projects/_models/activity';
import { useForm } from '@/hooks';
import type { projectCategoryEnum } from '@/utils/enum';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type FieldErrors,
  type UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const FORM_ITEM_WIDTH = 250;

type FormValues = z.infer<typeof UpdateConsecutiveIndicatorFormSchema>;

type Props = {
  evaluationDirection: EvaluationDirectionType | null | undefined;
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  initialTargetYears: number[];
  projectCategory: z.infer<typeof projectCategoryEnum> | null | undefined;
  onSubmit: (values: FormValues & { targetYears: number[] }) => void;
};

export const UpdateConsecutiveIndicatorForm: FC<Props> = ({
  evaluationDirection,
  fiscalYear,
  initialValues,
  initialTargetYears,
  projectCategory,
  onSubmit,
}) => {
  const [targetYears, setTargetYears] = useState<number[]>(initialTargetYears);
  const { t } = useTranslation();
  const {
    control,
    setValue,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    handleSubmit,
    clearErrors,
    setError,
    getValues,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      UpdateConsecutiveIndicatorFormSchema.refine(
        (data) => {
          if (
            data.type === 'outcome' &&
            data.dataNatureType === 'qualitative'
          ) {
            return !!data.statusOfAchievementWhenQualitative;
          }
          return true;
        },
        {
          path: ['statusOfAchievementWhenQualitative'],
          message: t('validation.required'),
        },
      ),
    ),
  });

  const { fields, append, prepend, remove } = useFieldArray<FormValues>({
    control,
    name: 'annualValues',
  });

  const isOutcome = initialValues?.type === 'outcome';

  /**定性的 */
  const isQualitativeOutcome = useMemo(() => {
    return isOutcome && initialValues?.dataNatureType === 'qualitative';
  }, [initialValues, isOutcome]);

  /**定量的 */
  const isQuantitativeOutcome = useMemo(() => {
    return isOutcome && initialValues?.dataNatureType === 'quantitative';
  }, [initialValues, isOutcome]);

  const handleAddConsecutiveIndicator = useCallback(
    (type: 'previous' | 'next') => {
      const isPrevious = type === 'previous';
      const field = getValues('annualValues').at(isPrevious ? 0 : -1);
      const year = field?.year ?? dayjs().year();
      const nextYear = isPrevious ? year - 1 : year + 1;

      const nextAnnualValueId = initialValues?.annualValues?.find(
        (consecutiveField) => consecutiveField.year === nextYear,
      )?.id;

      isPrevious
        ? prepend({
            id: nextAnnualValueId,
            actualValue: null,
            targetValue: null,
            achievementRate: null,
            year: nextYear,
          })
        : append({
            id: nextAnnualValueId,
            actualValue: null,
            targetValue: null,
            achievementRate: null,
            year: nextYear,
          });

      if (type === 'next' && isQuantitativeOutcome) {
        const lastYear = field?.year;
        if (!lastYear) {
          return;
        }
        setTargetYears((v) => [...v, lastYear + 1]);
      }
    },
    [getValues, initialValues, append, prepend, isQuantitativeOutcome],
  );

  // アウトプット且つ新規要求事業の場合は、次年度の指標を追加する
  useEffect(() => {
    if (
      initialValues?.type === 'output' &&
      projectCategory === 'requesting' &&
      initialValues.annualValues?.length === 1
    ) {
      const year = initialValues.annualValues.at(0)?.year;
      if (year === fiscalYear) {
        handleAddConsecutiveIndicator('next');
      }
    }
  }, [
    fiscalYear,
    initialValues,
    projectCategory,
    handleAddConsecutiveIndicator,
  ]);

  const handleRemoveConsecutiveIndicator = (index: number, year: number) => {
    remove(index);
    if (isQuantitativeOutcome) {
      setTargetYears((currentYears) => {
        const lastField = getValues('annualValues').at(-1);
        if (currentYears.length === 1 && lastField?.year === year) {
          return [lastField.year - 1];
        }
        return currentYears.filter((y) => y !== year);
      });
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit((values) => {
        let hasError = false;

        if (isQuantitativeOutcome) {
          if (!targetYears.length) {
            return;
          }

          for (const outcomeTargetYear of targetYears) {
            const annualValueByTargetYearsIndex = values.annualValues.findIndex(
              (v) => v.year === outcomeTargetYear,
            );

            const annualValueByTargetYears =
              values.annualValues[annualValueByTargetYearsIndex];

            if (typeof annualValueByTargetYears?.targetValue !== 'number') {
              setError(
                `annualValues.${annualValueByTargetYearsIndex}.targetValue`,
                {
                  type: 'required',
                  message: t('validation.required'),
                },
              );
              hasError = true;
            }
          }
          if (hasError) {
            return;
          }
          clearErrors();
        }

        if (initialValues?.type === 'output') {
          values.annualValues.forEach((annualValue, index) => {
            if (annualValue.targetValue === null) {
              setError(`annualValues.${index}.targetValue`, {
                type: 'required',
                message: t('validation.required'),
              });
              hasError = true;
            }
          });
          if (hasError) {
            return;
          }
          clearErrors();
        }

        onSubmit({
          ...values,
          targetYears: isQuantitativeOutcome ? targetYears : [],
        });
      })}
      style={{ maxWidth: 1000 }}
    >
      <TextInputField
        name="title"
        autocompleteKey="indicator.title"
        label={t('indicator-title')}
        control={control}
        errors={errors}
        disabled
      />

      <Box width={FORM_ITEM_WIDTH}>
        <TextInputField
          name="unit"
          label={t('unit')}
          control={control}
          errors={errors}
          disabled
        />
      </Box>

      <Divider size="lg" />

      <Flex vertical>
        <Button
          type="dashed"
          onClick={() => {
            handleAddConsecutiveIndicator('previous');
          }}
          block
          icon={<PlusOutlined aria-label="" />}
          disabled={isQualitativeOutcome}
        >
          {t('add-previous-consecutive-indicator')}
        </Button>
        <Box marginTop={16} marginBottom={16}>
          <Row gutter={4}>
            <Col span={isOutcome ? 5 : 4} />
            <Col span={isOutcome ? 6 : 9}>
              <Flex flex={1} align="center" gap={4}>
                <Text text={t('indicator-target-value')} />
                {(!isOutcome || isQuantitativeOutcome) && <RequiredTag />}
              </Flex>
            </Col>
            <Col span={isOutcome ? 6 : 9}>
              <Flex flex={1} align="center" gap={4}>
                <Text text={t('indicator-actual-value')} />
              </Flex>
            </Col>
            {initialValues?.type === 'outcome' && (
              <Col span={6}>
                <Text text={t('achievement-rate')} />
                <HintIcon hint={t('hint-achievement-rate')} />
              </Col>
            )}
            <Col span={isOutcome ? 1 : 2} />
          </Row>
        </Box>

        <Flex vertical gap={16}>
          {fields.map((item, index) => (
            <ConsecutiveIndicatorYearField
              key={item.id}
              evaluationDirection={evaluationDirection}
              fiscalYear={fiscalYear}
              index={index}
              control={control}
              errors={errors}
              annualValueLength={fields.length}
              year={item.year}
              type={initialValues?.type}
              term={initialValues?.term}
              dataNatureType={initialValues?.dataNatureType}
              disabled={isQualitativeOutcome}
              setValue={setValue}
              onRemove={handleRemoveConsecutiveIndicator}
              targetYears={targetYears}
              isLast={index === fields.length - 1}
              setTargetYears={setTargetYears}
              isQuantitativeOutcome={isQuantitativeOutcome}
            />
          ))}
        </Flex>
        {targetYears.length === 0 && isQuantitativeOutcome && (
          <Box marginTop={8}>
            <Text
              text={t('error-message.consecutive-indicator-select')}
              color="error"
              weight="regular"
            />
          </Box>
        )}
        <Button
          type="dashed"
          onClick={() => {
            handleAddConsecutiveIndicator('next');
          }}
          block
          icon={<PlusOutlined aria-label="" />}
          disabled={isQualitativeOutcome}
          style={{ marginTop: 16 }}
        >
          {t('add-next-consecutive-indicator')}
        </Button>
      </Flex>

      <Divider size="lg" />

      <TextAreaInputField
        name="statusOfAchievementWhenQualitative"
        control={control}
        errors={errors}
        label={t('achievement-for-qualitative-outcome')}
        hint={t('hint-achievement-for-qualitative-outcome')}
        rows={3}
        disabled={!isQualitativeOutcome}
        required={isQualitativeOutcome}
      />

      <SubmitButton />
    </Form>
  );
};

type ConsecutiveIndicatorYearFieldProps = Pick<
  Props,
  'fiscalYear' | 'evaluationDirection'
> & {
  index: number;
  control: FormControlProp<FormValues>;
  errors: FieldErrors;
  annualValueLength: number;
  year: number;
  type?: FormValues['type'];
  term?: FormValues['term'];
  dataNatureType?: FormValues['dataNatureType'];
  disabled?: boolean;
  setValue: UseFormSetValue<FormValues>;
  onRemove: (index: number, year: number) => void;
  targetYears: number[];
  isLast: boolean;
  setTargetYears: Dispatch<SetStateAction<number[]>>;
  isQuantitativeOutcome: boolean;
};

const ConsecutiveIndicatorYearField: FC<ConsecutiveIndicatorYearFieldProps> = ({
  evaluationDirection,
  fiscalYear,
  index,
  control,
  annualValueLength,
  type,
  term,
  dataNatureType,
  year,
  errors,
  disabled,
  onRemove,
  setValue,
  targetYears,
  isLast,
  setTargetYears,
  isQuantitativeOutcome,
}) => {
  const { t } = useTranslation();

  const [targetValue, actualValue] = useWatch({
    control,
    name: [
      `annualValues.${index}.targetValue`,
      `annualValues.${index}.actualValue`,
    ],
  });

  const isOutcome = type === 'outcome';
  const isLastYear = index === annualValueLength - 1;
  const yearPrefixText =
    (targetYears.includes(year) || isLast) &&
    type === 'outcome' &&
    dataNatureType === 'quantitative'
      ? parseTargetFiscalYearTitle({
          term,
          isLastTargetYear: targetYears.sort((a, b) => a - b).at(-1) === year,
          isTargetYear: targetYears.includes(year),
          dataNatureType,
        })
      : null;

  return (
    <Row gutter={8}>
      <Col
        span={5}
        style={{
          display: 'flex',
          alignItems: isQuantitativeOutcome ? 'flex-start' : 'center',
        }}
      >
        <Flex gap={8}>
          {isQuantitativeOutcome ? (
            <Checkbox
              checked={targetYears.includes(year)}
              onChange={(e) => {
                const checked = e.target.checked;
                setTargetYears((v) => {
                  if (checked) {
                    return [...v, year];
                  }
                  return v.filter((v) => v !== year);
                });
              }}
            />
          ) : null}
          <Flex vertical>
            {yearPrefixText && (
              <Flex gap={4}>
                <Text text={yearPrefixText} />
                {index === 0 && (
                  <HintIcon hint={t('hint-target-fiscal-year')} />
                )}
              </Flex>
            )}
            <Text text={t('fiscal-year-with-value', { year })} />
          </Flex>
        </Flex>
      </Col>
      <Col span={isOutcome ? 6 : 9}>
        <NumericFormatField
          control={control}
          name={`annualValues.${index}.targetValue`}
          errors={errors}
          decimalScale={3}
          shouldCoexistOnChange
          onChange={(value) => {
            if (type === 'outcome') {
              const rate = rateByEvaluationDirection({
                evaluationDirection,
                targetValue: value ?? null,
                actualValue,
              });
              if (typeof rate === 'number') {
                setValue(`annualValues.${index}.achievementRate`, rate);
              }
              setValue(`annualValues.${index}.isManualAchievementRate`, false);
            }
          }}
          disabled={disabled}
          marginBottom={0}
          width="100%"
        />
      </Col>
      <Col span={isOutcome ? 6 : 9}>
        <NumericFormatField
          control={control}
          name={`annualValues.${index}.actualValue`}
          errors={errors}
          decimalScale={3}
          shouldCoexistOnChange
          onChange={(value) => {
            if (type === 'outcome') {
              const rate = rateByEvaluationDirection({
                evaluationDirection,
                targetValue,
                actualValue: value,
              });

              if (typeof rate === 'number') {
                setValue(`annualValues.${index}.achievementRate`, rate);
              }
              setValue(`annualValues.${index}.isManualAchievementRate`, false);
            }
          }}
          disabled={disabled}
          marginBottom={0}
          width="100%"
        />
      </Col>
      {isOutcome && (
        <Col span={6}>
          <NumericFormatField
            control={control}
            name={`annualValues.${index}.achievementRate`}
            errors={errors}
            decimalScale={1}
            addonAfter={t('percentage-unit')}
            shouldCoexistOnChange
            onChange={() => {
              setValue(`annualValues.${index}.isManualAchievementRate`, true);
            }}
            disabled={disabled}
            marginBottom={0}
            width="100%"
          />
        </Col>
      )}
      <Col span={1} style={{ marginTop: 4 }}>
        {(isQuantitativeOutcome || !isOutcome) &&
          year !== fiscalYear &&
          (isLastYear || index === 0) && (
            <MinusCircleOutlined
              onClick={() => {
                onRemove(index, year);
              }}
            />
          )}
      </Col>
    </Row>
  );
};
